import React, { useEffect, useMemo, useState } from 'react';
import SelectorHora from './SelectorHora';
import { Copy, Plus, Trash2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { COLORES, DIAS_CLAVE, DIAS_NOMBRE, capital, listaZonas, nombreZona, textoDia } from '../../lib/calendario';
import { CORREO_URL } from '../../lib/dominios';

const HORARIO_DEFECTO = Object.fromEntries(DIAS_CLAVE.map((d) => [d, ['sab', 'dom'].includes(d) ? [] : [['09:00', '17:00']]]));

export function paginaAForm(p, { zona, calendarioId }) {
  return {
    titulo: p?.titulo || '',
    slug: p?.slug || '',
    descripcion: p?.descripcion || '',
    ubicacion: p?.ubicacion || '',
    color: p?.color || COLORES[0].valor,
    zona: p?.zona || zona,
    duracion: p?.duracion ?? 30,
    intervalo: p?.intervalo ?? (p?.duracion ?? 30),
    margen_antes: p?.margen_antes ?? 0,
    margen_despues: p?.margen_despues ?? 0,
    aviso_minimo: p?.aviso_minimo ?? 240,
    ventana_dias: p?.ventana_dias ?? 60,
    max_por_dia: p?.max_por_dia ?? 0,
    horario: JSON.parse(JSON.stringify(p?.horario || HORARIO_DEFECTO)),
    fechas_bloqueadas: [...(p?.fechas_bloqueadas || [])],
    preguntas: (p?.preguntas || []).map((q) => ({ ...q })),
    pedir_telefono: !!p?.pedir_telefono,
    videollamada: !!p?.videollamada,
    activa: p ? p.activa !== false : true,
    calendario_id: p?.calendario_id || calendarioId,
    bloquean: p?.bloquean ? [...p.bloquean] : null,
    recordatorio_horas: p?.recordatorio_horas ?? 24,
  };
}

const DURACIONES = [15, 20, 30, 45, 60, 90, 120];
const AVISOS = [[0, 'Sin aviso mínimo'], [30, '30 minutos'], [60, '1 hora'], [120, '2 horas'], [240, '4 horas'], [720, '12 horas'], [1440, '1 día'], [2880, '2 días'], [10080, '1 semana']];

/** Crear o editar una página de reserva. `onGuardar(form)` lanza un Error con el mensaje si falla. */
export default function EditorPagina({ abierto, pagina, inicial, calendarios, propios, onCerrar, onGuardar }) {
  const [f, setF] = useState(inicial);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [fechaNueva, setFechaNueva] = useState('');
  const zonas = useMemo(() => listaZonas(), []);
  useEffect(() => { if (abierto) { setF(inicial); setError(''); } }, [abierto, inicial]);
  if (!abierto || !f) return null;
  const set = (c) => setF((x) => ({ ...x, ...c }));
  const bloquean = f.bloquean || propios.map((c) => c.id);

  const setTramo = (dia, i, j, valor) => {
    const h = { ...f.horario, [dia]: f.horario[dia].map((t, k) => (k === i ? (j === 0 ? [valor, t[1]] : [t[0], valor]) : t)) };
    set({ horario: h });
  };

  const guardar = async () => {
    setError('');
    if (!f.titulo.trim()) { setError('Ponle un título a la página.'); return; }
    for (const d of DIAS_CLAVE) {
      for (const [a, b] of f.horario[d] || []) {
        if (!a || !b || b <= a) { setError(`Revisa el horario del ${DIAS_NOMBRE[DIAS_CLAVE.indexOf(d)]}: cada tramo debe terminar después de empezar.`); return; }
      }
    }
    setGuardando(true);
    try {
      await onGuardar({ ...f, bloquean, slug: f.slug.trim().toLowerCase() || undefined });
    } catch (e) {
      setError(e.message || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const base = `${(CORREO_URL || window.location.origin).replace(/^https?:\/\//, '')}/reservar/`;

  return (
    <Dialog open={abierto} onOpenChange={(v) => { if (!v) onCerrar(); }}>
      <DialogContent className="cal-editor p-0 gap-0 [&>button.absolute]:hidden" data-testid="cal-editor-pagina">
        <DialogTitle className="sr-only">{pagina ? 'Editar página de reserva' : 'Nueva página de reserva'}</DialogTitle>
        <header className="cal-editor-cab">
          <button type="button" className="cal-icono-btn" onClick={onCerrar} aria-label="Cerrar"><X className="h-5 w-5" /></button>
          <input className="cal-titulo-input flex-1" value={f.titulo} onChange={(e) => set({ titulo: e.target.value })} placeholder="Título de la cita (p. ej. Consulta de 30 minutos)" aria-label="Título" autoFocus={!pagina} data-testid="cal-pagina-titulo" />
          <button type="button" className="od-btn-primary" onClick={guardar} disabled={guardando} data-testid="cal-pagina-guardar">{guardando ? 'Guardando…' : 'Guardar'}</button>
        </header>
        <div className="cal-editor-cuerpo od-scroll">
          {error && <div className="cal-error" role="alert">{error}</div>}
          <div className="cal-editor-columnas cal-editor-columnas--par">
            <div className="cal-editor-col">
              <div className="cal-seccion-titulo">La cita</div>
              <label className="cal-etiqueta">Enlace
                <div className="cal-slug">
                  <span className="truncate">{base}</span>
                  <input value={f.slug} onChange={(e) => set({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} placeholder="se crea solo" aria-label="Enlace" data-testid="cal-pagina-slug" />
                </div>
                <span className="cal-ayuda">Minúsculas, números y guiones. Si lo dejas vacío se forma con tu usuario y el título.</span>
              </label>
              <label className="cal-etiqueta">Descripción
                <textarea className="cal-input" rows={3} value={f.descripcion} onChange={(e) => set({ descripcion: e.target.value })} placeholder="Qué es la cita y qué hay que traer" />
              </label>
              <label className="cal-etiqueta">Ubicación
                <input className="cal-input" value={f.ubicacion} onChange={(e) => set({ ubicacion: e.target.value })} placeholder="Dirección, teléfono o enlace de videollamada" />
              </label>
              <div className="cal-etiqueta">Color
                <div className="cal-colores">
                  {COLORES.map((c) => (
                    <button type="button" key={c.valor} className={`cal-color ${f.color === c.valor ? 'is-sel' : ''}`} style={{ background: c.valor }} onClick={() => set({ color: c.valor })} title={c.nombre} aria-label={c.nombre} />
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="cal-etiqueta">Duración
                  <select className="cal-select" value={f.duracion} onChange={(e) => { const d = Number(e.target.value); set({ duracion: d, intervalo: f.intervalo === f.duracion ? d : f.intervalo }); }} data-testid="cal-pagina-duracion">
                    {[...new Set([...DURACIONES, f.duracion])].sort((a, b) => a - b).map((d) => <option key={d} value={d}>{d < 60 ? `${d} min` : `${d / 60} h`}</option>)}
                  </select>
                </label>
                <label className="cal-etiqueta">Una cita cada
                  <select className="cal-select" value={f.intervalo} onChange={(e) => set({ intervalo: Number(e.target.value) })}>
                    {[...new Set([10, 15, 20, 30, 45, 60, 90, 120, f.intervalo, f.duracion])].sort((a, b) => a - b).map((d) => <option key={d} value={d}>{d < 60 ? `${d} min` : `${d / 60} h`}</option>)}
                  </select>
                </label>
                <label className="cal-etiqueta">Margen antes
                  <input type="number" min="0" max="240" className="cal-input" value={f.margen_antes} onChange={(e) => set({ margen_antes: Number(e.target.value) || 0 })} />
                </label>
                <label className="cal-etiqueta">Margen después
                  <input type="number" min="0" max="240" className="cal-input" value={f.margen_despues} onChange={(e) => set({ margen_despues: Number(e.target.value) || 0 })} />
                </label>
                <label className="cal-etiqueta">Aviso mínimo
                  <select className="cal-select" value={f.aviso_minimo} onChange={(e) => set({ aviso_minimo: Number(e.target.value) })}>
                    {(AVISOS.some(([v]) => v === f.aviso_minimo) ? AVISOS : [...AVISOS, [f.aviso_minimo, `${f.aviso_minimo} min`]].sort((a, b) => a[0] - b[0])).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                  </select>
                </label>
                <label className="cal-etiqueta">Se puede reservar hasta
                  <div className="flex items-center gap-2">
                    <input type="number" min="1" max="365" className="cal-input w-[80px]" value={f.ventana_dias} onChange={(e) => set({ ventana_dias: Number(e.target.value) || 1 })} />
                    <span className="text-[13px]" style={{ color: 'var(--text-2)' }}>días antes</span>
                  </div>
                </label>
                <label className="cal-etiqueta">Máximo de citas al día
                  <input type="number" min="0" max="100" className="cal-input" value={f.max_por_dia} onChange={(e) => set({ max_por_dia: Number(e.target.value) || 0 })} />
                  <span className="cal-ayuda">0 = sin límite</span>
                </label>
                <label className="cal-etiqueta">Zona horaria
                  <select className="cal-select" value={f.zona} onChange={(e) => set({ zona: e.target.value })}>
                    {zonas.map((z) => <option key={z} value={z}>{nombreZona(z)}</option>)}
                  </select>
                </label>
              </div>

              <div className="cal-seccion-titulo mt-2">Videollamada</div>
              <label className="cal-check" data-testid="cal-pagina-videollamada">
                <input type="checkbox" checked={f.videollamada} onChange={(e) => set({ videollamada: e.target.checked })} />
                Añadir una videollamada de Orvian Meet a cada cita
              </label>
              <p className="cal-ayuda">Cada cita tiene su propio enlace; llega en la confirmación, en la invitación y en tu calendario.</p>

              <div className="cal-seccion-titulo mt-2">Lo que se le pide a quien reserva</div>
              <p className="cal-ayuda">Siempre se piden el nombre y el correo.</p>
              <label className="cal-check"><input type="checkbox" checked={f.pedir_telefono} onChange={(e) => set({ pedir_telefono: e.target.checked })} /> Pedir teléfono</label>
              {f.preguntas.map((q, i) => (
                <div key={i} className="cal-pregunta">
                  <input className="cal-input flex-1" value={q.etiqueta} onChange={(e) => set({ preguntas: f.preguntas.map((x, j) => (j === i ? { ...x, etiqueta: e.target.value } : x)) })} placeholder="Pregunta" aria-label="Pregunta" />
                  <select className="cal-select" value={q.tipo} onChange={(e) => set({ preguntas: f.preguntas.map((x, j) => (j === i ? { ...x, tipo: e.target.value } : x)) })} aria-label="Tipo de respuesta">
                    <option value="texto">Respuesta corta</option>
                    <option value="parrafo">Párrafo</option>
                    <option value="telefono">Teléfono</option>
                  </select>
                  <label className="cal-check"><input type="checkbox" checked={!!q.obligatoria} onChange={(e) => set({ preguntas: f.preguntas.map((x, j) => (j === i ? { ...x, obligatoria: e.target.checked } : x)) })} /> Obligatoria</label>
                  <button type="button" className="cal-icono-btn" onClick={() => set({ preguntas: f.preguntas.filter((_, j) => j !== i) })} aria-label="Quitar pregunta"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              {f.preguntas.length < 10 && (
                <button type="button" className="cal-mini-enlace self-start" onClick={() => set({ preguntas: [...f.preguntas, { etiqueta: '', tipo: 'texto', obligatoria: false }] })}><Plus className="h-3.5 w-3.5" /> Añadir pregunta</button>
              )}
            </div>

            <div className="cal-editor-col">
              <div className="cal-seccion-titulo">Horario disponible</div>
              <div className="cal-horario" data-testid="cal-pagina-horario">
                {DIAS_CLAVE.map((d, idx) => {
                  const tramos = f.horario[d] || [];
                  return (
                    <div key={d} className="cal-horario-dia">
                      <label className="cal-check cal-horario-nombre">
                        <input type="checkbox" checked={tramos.length > 0} onChange={(e) => set({ horario: { ...f.horario, [d]: e.target.checked ? [['09:00', '17:00']] : [] } })} />
                        {capital(DIAS_NOMBRE[idx]).slice(0, 3)}
                      </label>
                      <div className="flex-1 flex flex-col gap-1.5">
                        {tramos.length === 0 && <span className="cal-ayuda pt-1.5">No disponible</span>}
                        {tramos.map((t, i) => (
                          <div key={i} className="cal-tramo">
                            <SelectorHora value={t[0]} paso={15} onChange={(v) => setTramo(d, i, 0, v)} ariaLabel="Desde" />
                            <span>–</span>
                            <SelectorHora value={t[1] === '24:00' ? '23:59' : t[1]} paso={15} onChange={(v) => setTramo(d, i, 1, v)} ariaLabel="Hasta" />
                            <button type="button" className="cal-icono-btn" onClick={() => set({ horario: { ...f.horario, [d]: tramos.filter((_, j) => j !== i) } })} aria-label="Quitar tramo"><X className="h-4 w-4" /></button>
                          </div>
                        ))}
                      </div>
                      <div className="flex">
                        <button type="button" className="cal-icono-btn" title="Añadir otro tramo" aria-label="Añadir tramo" onClick={() => {
                          const ult = tramos[tramos.length - 1];
                          const nuevo = ult ? [ult[1] < '23:00' ? ult[1] : '18:00', ult[1] < '22:00' ? `${String(Number(ult[1].slice(0, 2)) + 1).padStart(2, '0')}:${ult[1].slice(3)}` : '23:00'] : ['09:00', '17:00'];
                          set({ horario: { ...f.horario, [d]: [...tramos, nuevo] } });
                        }}
                        ><Plus className="h-4 w-4" /></button>
                        <button type="button" className="cal-icono-btn" title="Copiar este horario a todos los días" aria-label="Copiar a todos los días" onClick={() => set({ horario: Object.fromEntries(DIAS_CLAVE.map((x) => [x, (f.horario[x] || []).length || x === d ? tramos.map((t) => [...t]) : []])) })}>
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="cal-seccion-titulo mt-2">Días bloqueados</div>
              <div className="flex gap-2">
                <input type="date" className="cal-input" value={fechaNueva} onChange={(e) => setFechaNueva(e.target.value)} aria-label="Día bloqueado" />
                <button type="button" className="od-btn-secondary" disabled={!fechaNueva} onClick={() => { set({ fechas_bloqueadas: [...new Set([...f.fechas_bloqueadas, fechaNueva])].sort() }); setFechaNueva(''); }}>Añadir</button>
              </div>
              {f.fechas_bloqueadas.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {f.fechas_bloqueadas.map((d) => (
                    <span key={d} className="cal-chip">
                      {textoDia(d, { day: 'numeric', month: 'short', year: 'numeric' })}
                      <button type="button" onClick={() => set({ fechas_bloqueadas: f.fechas_bloqueadas.filter((x) => x !== d) })} aria-label="Quitar"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}

              <div className="cal-seccion-titulo mt-2">Calendarios</div>
              <label className="cal-etiqueta">Las citas se guardan en
                <select className="cal-select" value={f.calendario_id || ''} onChange={(e) => set({ calendario_id: e.target.value })}>
                  {calendarios.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </label>
              <div className="cal-etiqueta">Restan horas los eventos de
                <div className="flex flex-col gap-1">
                  {propios.map((c) => (
                    <label key={c.id} className="cal-check">
                      <input
                        type="checkbox"
                        checked={bloquean.includes(c.id) || c.id === f.calendario_id}
                        disabled={c.id === f.calendario_id}
                        onChange={(e) => set({ bloquean: e.target.checked ? [...bloquean, c.id] : bloquean.filter((x) => x !== c.id) })}
                      />
                      <span className="cal-cuadro cal-cuadro--chico" style={{ background: c.color }} />{c.nombre}
                    </label>
                  ))}
                </div>
                <span className="cal-ayuda">Tus eventos «Ocupado» de esos calendarios y las invitaciones aceptadas no se ofrecen como hueco.</span>
              </div>
              <label className="cal-etiqueta">Recordatorio por correo a quien reserva
                <select className="cal-select" value={f.recordatorio_horas} onChange={(e) => set({ recordatorio_horas: Number(e.target.value) })}>
                  {[[0, 'No enviar'], [1, '1 hora antes'], [2, '2 horas antes'], [12, '12 horas antes'], [24, '1 día antes'], [48, '2 días antes'], [168, '1 semana antes']].map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                </select>
              </label>
              <label className="cal-check"><input type="checkbox" checked={f.activa} onChange={(e) => set({ activa: e.target.checked })} /> Página activa (se pueden hacer reservas)</label>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
